import type { AMMCreate, AMMDeposit, AMMWithdraw, Transaction } from 'xrpl/dist/npm'
import { AMMDepositFlags } from 'xrpl/dist/npm/models/transactions/AMMDeposit'
import { getXrplClient } from './client'
import { fromXrplAmount, toXrplAmount, toXrplAsset } from './amounts'
import { XrplCurrency, XrplSubmitResult } from './types'

export type AmmInfo = {
  assetA: XrplCurrency
  assetB: XrplCurrency
  reserveA: string
  reserveB: string
  tradingFee: number
  lpToken: {
    currency: string
    issuer: string
  }
}

const matchesAsset = (currency: XrplCurrency, asset: { currency: string; issuer?: string }) => {
  if (currency.isNative || currency.currency === 'XRP') {
    return asset.currency === 'XRP'
  }
  return asset.currency === currency.currency && asset.issuer === currency.issuer
}

export const getAmmInfo = async (assetA: XrplCurrency, assetB: XrplCurrency): Promise<AmmInfo | null> => {
  const client = await getXrplClient()

  try {
    const response = await client.request({
      command: 'amm_info',
      asset: toXrplAsset(assetA),
      asset2: toXrplAsset(assetB),
    })

    const amm = response.result.amm
    if (!amm) {
      return null
    }

    const reserveA = fromXrplAmount(amm.amount)
    const reserveB = fromXrplAmount(amm.amount2)

    const lpToken = {
      currency: amm.lp_token.currency,
      issuer: amm.lp_token.issuer,
    }

    return {
      assetA,
      assetB,
      reserveA,
      reserveB,
      tradingFee: Number(amm.trading_fee || 0),
      lpToken,
    }
  } catch (error) {
    return null
  }
}

export const quoteAmmSwap = ({
  amm,
  amountIn,
  from,
  to,
}: {
  amm: AmmInfo
  amountIn: string
  from: XrplCurrency
  to: XrplCurrency
}) => {
  const feeRate = amm.tradingFee / 100000
  const reserveIn = matchesAsset(from, toXrplAsset(amm.assetA)) ? Number(amm.reserveA) : Number(amm.reserveB)
  const reserveOut = matchesAsset(from, toXrplAsset(amm.assetA)) ? Number(amm.reserveB) : Number(amm.reserveA)

  if (!reserveIn || !reserveOut) {
    return '0'
  }

  const inputAmount = Number(amountIn || '0')
  if (!inputAmount) {
    return '0'
  }

  const effectiveIn = inputAmount * (1 - feeRate)
  const newReserveIn = reserveIn + effectiveIn
  const newReserveOut = (reserveIn * reserveOut) / newReserveIn
  const output = reserveOut - newReserveOut

  return output > 0 ? output.toString() : '0'
}

type XrplSignAndSubmit = (tx: Transaction) => Promise<XrplSubmitResult>

export const createAmmPool = async ({
  account,
  assetA,
  assetB,
  amountA,
  amountB,
  tradingFee,
  signAndSubmit,
}: {
  account: string
  assetA: XrplCurrency
  assetB: XrplCurrency
  amountA: string
  amountB: string
  tradingFee?: number
  signAndSubmit: XrplSignAndSubmit
}) => {
  const tx: AMMCreate = {
    TransactionType: 'AMMCreate',
    Account: account,
    Amount: toXrplAmount(assetA, amountA),
    Amount2: toXrplAmount(assetB, amountB),
  }

  if (typeof tradingFee === 'number') {
    tx.TradingFee = tradingFee
  }

  return signAndSubmit(tx)
}

export const depositAmmLiquidity = async ({
  account,
  assetA,
  assetB,
  amountA,
  amountB,
  amm,
  signAndSubmit,
}: {
  account: string
  assetA: XrplCurrency
  assetB: XrplCurrency
  amountA: string
  amountB: string
  amm?: { assetA: XrplCurrency; assetB: XrplCurrency } | null
  signAndSubmit: XrplSignAndSubmit
}) => {
  let depositAssetA = assetA
  let depositAssetB = assetB
  let depositAmountA = amountA
  let depositAmountB = amountB

  if (amm?.assetA && amm?.assetB) {
    const ammAssetA = toXrplAsset(amm.assetA)
    const ammAssetB = toXrplAsset(amm.assetB)
    const assetAMatchesA = matchesAsset(assetA, ammAssetA)
    const assetBMatchesB = matchesAsset(assetB, ammAssetB)
    const assetAMatchesB = matchesAsset(assetA, ammAssetB)
    const assetBMatchesA = matchesAsset(assetB, ammAssetA)

    if (assetAMatchesB && assetBMatchesA) {
      depositAssetA = assetB
      depositAssetB = assetA
      depositAmountA = amountB
      depositAmountB = amountA
    } else if (assetAMatchesA && assetBMatchesB) {
      depositAssetA = assetA
      depositAssetB = assetB
      depositAmountA = amountA
      depositAmountB = amountB
    }
  }

  const tx: AMMDeposit = {
    TransactionType: 'AMMDeposit',
    Account: account,
    Asset: toXrplAsset(depositAssetA),
    Asset2: toXrplAsset(depositAssetB),
    Amount: toXrplAmount(depositAssetA, depositAmountA),
    Amount2: toXrplAmount(depositAssetB, depositAmountB),
    Flags: AMMDepositFlags.tfTwoAsset,
  }

  return signAndSubmit(tx)
}

export const withdrawAmmLiquidity = async ({
  account,
  assetA,
  assetB,
  lpToken,
  lpAmount,
  amm,
  signAndSubmit,
}: {
  account: string
  assetA: XrplCurrency
  assetB: XrplCurrency
  lpToken: { currency: string; issuer: string }
  lpAmount: string
  amm?: { assetA: XrplCurrency; assetB: XrplCurrency } | null
  signAndSubmit: XrplSignAndSubmit
}) => {
  const withdrawAssetA = amm?.assetA || assetA
  const withdrawAssetB = amm?.assetB || assetB
  const tx: AMMWithdraw = {
    TransactionType: 'AMMWithdraw',
    Account: account,
    Asset: toXrplAsset(withdrawAssetA),
    Asset2: toXrplAsset(withdrawAssetB),
    LPTokenIn: {
      currency: lpToken.currency,
      issuer: lpToken.issuer,
      value: lpAmount,
    },
  }

  return signAndSubmit(tx)
}
